import { create } from "zustand";
import { CNS_API_BASE } from "@/config";
import { throwOnProblemResponse } from "@/types/problem-detail";
import type {
  ConversationLineageEdgeChange,
  LineageEdge,
} from "@/services/lineageEdgeService";

/** Stable empty reference for Zustand selectors (React 19 useSyncExternalStore). */
export const EMPTY_CONVERSATION_LINEAGE_EDGES: LineageEdge[] = [];

export function conversationLineageKey(
  projectId: string,
  conversationId: string,
): string {
  return `${projectId}:${conversationId}`;
}

export function conversationLineageSubjectKey(
  edge: Pick<LineageEdge, "subjectType" | "subjectPublicId">,
): string {
  return `${edge.subjectType}:${edge.subjectPublicId}`;
}

function conversationSubjectSlotKey(
  edge: Pick<
    LineageEdge,
    "subjectType" | "subjectPublicId" | "targetType" | "targetPublicId"
  >,
): string {
  return `${edge.subjectType}:${edge.subjectPublicId}:${edge.targetType}:${edge.targetPublicId}`;
}

function sameConversationSubject(
  edge: LineageEdge,
  change: Pick<
    ConversationLineageEdgeChange,
    "subjectType" | "subjectPublicId" | "targetType" | "targetPublicId"
  >,
): boolean {
  return conversationSubjectSlotKey(edge) === conversationSubjectSlotKey(change);
}

interface ConversationLineageState {
  edges: LineageEdge[];
  /** True only for the first load (no prior entry). Background force-refresh keeps edges on screen. */
  loading: boolean;
  error: string | null;
}

export interface FetchConversationLineageOptions {
  force?: boolean;
}

interface ConversationLineageStore {
  byKey: Record<string, ConversationLineageState | undefined>;
  inflightByKey: Record<string, Promise<LineageEdge[]> | undefined>;
  /** Monotonic per-conversation epoch; stale in-flight GET responses are dropped. */
  fetchEpochByKey: Record<string, number>;
  recentRealtimeUpsertIdsByKey: Record<string, Set<string> | undefined>;
  deletedEdgeIdsByKey: Record<string, Set<string> | undefined>;
  getEdges: (projectId: string | null, conversationId: string | null) => LineageEdge[];
  getSubjectKeys: (
    projectId: string | null,
    conversationId: string | null,
  ) => Set<string>;
  hasLoaded: (projectId: string, conversationId: string) => boolean;
  fetchConversationLineage: (
    projectId: string,
    conversationId: string,
    options?: FetchConversationLineageOptions,
  ) => Promise<LineageEdge[]>;
  applyEdgeChange: (change: ConversationLineageEdgeChange) => void;
  /** True discard (leave conversation / reset). Bumps epoch so orphan GETs cannot repopulate. */
  clearConversation: (projectId: string, conversationId: string) => void;
  /** Document-store style: force refetch while keeping current edges visible. */
  invalidateConversation: (
    projectId: string,
    conversationId: string,
  ) => Promise<LineageEdge[]>;
  invalidateAllLoadedConversations: () => Promise<void>;
}

const emptyState = (): ConversationLineageState => ({
  edges: EMPTY_CONVERSATION_LINEAGE_EDGES,
  loading: false,
  error: null,
});

function markRealtimeUpsert(
  recentByKey: Record<string, Set<string> | undefined>,
  key: string,
  edgeId: string,
): Record<string, Set<string> | undefined> {
  const next = new Set(recentByKey[key] ?? []);
  next.add(edgeId);
  return { ...recentByKey, [key]: next };
}

/**
 * Prefer server edges; keep only recent realtime attaches not yet listed;
 * never resurrect tombstoned ids. Canonicalize one edge per conversation subject slot.
 */
export function mergeFetchedConversationLineage(
  local: LineageEdge[],
  fetched: LineageEdge[],
  recentRealtimeUpsertIds: Set<string> | undefined,
  deletedEdgeIds: Set<string> | undefined,
): LineageEdge[] {
  const byId = new Map<string, LineageEdge>();
  for (const edge of fetched) {
    if (deletedEdgeIds?.has(edge.id)) continue;
    byId.set(edge.id, edge);
  }
  for (const edge of local) {
    if (byId.has(edge.id) || deletedEdgeIds?.has(edge.id)) continue;
    if (recentRealtimeUpsertIds?.has(edge.id)) {
      byId.set(edge.id, edge);
    }
  }
  return canonicalizeBySubjectSlot([...byId.values()], recentRealtimeUpsertIds);
}

function canonicalizeBySubjectSlot(
  edges: LineageEdge[],
  preferIds?: Set<string>,
): LineageEdge[] {
  const bySlot = new Map<string, LineageEdge>();
  for (const edge of edges) {
    const slot = conversationSubjectSlotKey(edge);
    const existing = bySlot.get(slot);
    if (!existing) {
      bySlot.set(slot, edge);
      continue;
    }
    if (preferIds?.has(edge.id) && !preferIds.has(existing.id)) {
      bySlot.set(slot, edge);
    }
  }
  return [...bySlot.values()];
}

function reconcileRealtimeTracking(
  fetched: LineageEdge[],
  recentRealtimeUpsertIds: Set<string> | undefined,
  deletedEdgeIds: Set<string> | undefined,
): {
  recentRealtimeUpsertIds?: Set<string>;
  deletedEdgeIds?: Set<string>;
} {
  const fetchedIds = new Set(fetched.map((edge) => edge.id));
  let nextRecent = recentRealtimeUpsertIds;
  if (recentRealtimeUpsertIds) {
    const filtered = new Set(
      [...recentRealtimeUpsertIds].filter((id) => !fetchedIds.has(id)),
    );
    nextRecent = filtered.size > 0 ? filtered : undefined;
  }
  let nextDeleted = deletedEdgeIds;
  if (deletedEdgeIds) {
    // Keep tombstones only while the stale server list still contains the id.
    const filtered = new Set(
      [...deletedEdgeIds].filter((id) => fetchedIds.has(id)),
    );
    nextDeleted = filtered.size > 0 ? filtered : undefined;
  }
  return {
    recentRealtimeUpsertIds: nextRecent,
    deletedEdgeIds: nextDeleted,
  };
}

async function fetchConversationLineageFromApi(
  projectId: string,
  conversationId: string,
): Promise<LineageEdge[]> {
  const res = await fetch(
    `${CNS_API_BASE}/projects/${projectId}/conversations/${conversationId}/lineage-edges`,
    { credentials: "include" },
  );
  if (!res.ok) await throwOnProblemResponse(res, "Failed to load sources");
  return res.json();
}

export function selectConversationLineageEdges(
  state: ConversationLineageStore,
  projectId: string | null | undefined,
  conversationId: string | null | undefined,
): LineageEdge[] {
  if (!projectId || !conversationId) {
    return EMPTY_CONVERSATION_LINEAGE_EDGES;
  }
  const key = conversationLineageKey(projectId, conversationId);
  return state.byKey[key]?.edges ?? EMPTY_CONVERSATION_LINEAGE_EDGES;
}

export function selectConversationLineageSubjectKeys(
  state: ConversationLineageStore,
  projectId: string | null | undefined,
  conversationId: string | null | undefined,
): Set<string> {
  const edges = selectConversationLineageEdges(state, projectId, conversationId);
  return new Set(edges.map((edge) => conversationLineageSubjectKey(edge)));
}

function splitConversationLineageKey(
  key: string,
): [string | null, string | null] {
  const separator = key.indexOf(":");
  if (separator <= 0 || separator === key.length - 1) {
    return [null, null];
  }
  return [key.slice(0, separator), key.slice(separator + 1)];
}

export const useConversationLineageStore = create<ConversationLineageStore>(
  (set, get) => ({
    byKey: {},
    inflightByKey: {},
    fetchEpochByKey: {},
    recentRealtimeUpsertIdsByKey: {},
    deletedEdgeIdsByKey: {},

    getEdges: (projectId, conversationId) =>
      selectConversationLineageEdges(get(), projectId, conversationId),

    getSubjectKeys: (projectId, conversationId) =>
      selectConversationLineageSubjectKeys(get(), projectId, conversationId),

    hasLoaded: (projectId, conversationId) => {
      const key = conversationLineageKey(projectId, conversationId);
      return get().byKey[key] !== undefined;
    },

    fetchConversationLineage: async (projectId, conversationId, options = {}) => {
      const key = conversationLineageKey(projectId, conversationId);
      const force = Boolean(options.force);

      if (!force) {
        const cached = get().byKey[key];
        if (cached && !cached.loading) {
          return cached.edges;
        }
        const inflight = get().inflightByKey[key];
        if (inflight) {
          return inflight;
        }
      }

      // force:true always starts a new GET. Bumping epoch drops any prior in-flight write-back
      // (same contract as useWorkflowSnapshotStore).
      const epoch = (get().fetchEpochByKey[key] ?? 0) + 1;
      const existing = get().byKey[key];
      const isBackgroundRefresh = force && existing !== undefined;

      set((state) => ({
        fetchEpochByKey: {
          ...state.fetchEpochByKey,
          [key]: epoch,
        },
        byKey: {
          ...state.byKey,
          [key]: {
            ...(existing ?? emptyState()),
            loading: !isBackgroundRefresh,
            error: null,
          },
        },
      }));

      const request = fetchConversationLineageFromApi(projectId, conversationId)
        .then((fetched) => {
          set((state) => {
            if (state.fetchEpochByKey[key] !== epoch) {
              return state;
            }
            const local =
              state.byKey[key]?.edges ?? EMPTY_CONVERSATION_LINEAGE_EDGES;
            const merged = mergeFetchedConversationLineage(
              local,
              fetched,
              state.recentRealtimeUpsertIdsByKey[key],
              state.deletedEdgeIdsByKey[key],
            );
            const tracking = reconcileRealtimeTracking(
              fetched,
              state.recentRealtimeUpsertIdsByKey[key],
              state.deletedEdgeIdsByKey[key],
            );
            return {
              byKey: {
                ...state.byKey,
                [key]: {
                  edges: merged,
                  loading: false,
                  error: null,
                },
              },
              inflightByKey: {
                ...state.inflightByKey,
                [key]: undefined,
              },
              recentRealtimeUpsertIdsByKey: {
                ...state.recentRealtimeUpsertIdsByKey,
                [key]: tracking.recentRealtimeUpsertIds,
              },
              deletedEdgeIdsByKey: {
                ...state.deletedEdgeIdsByKey,
                [key]: tracking.deletedEdgeIds,
              },
            };
          });
          return get().byKey[key]?.edges ?? fetched;
        })
        .catch((err: unknown) => {
          const message =
            err instanceof Error ? err.message : "Failed to load sources";
          set((state) => {
            if (state.fetchEpochByKey[key] !== epoch) {
              return state;
            }
            return {
              byKey: {
                ...state.byKey,
                [key]: {
                  edges:
                    state.byKey[key]?.edges ?? EMPTY_CONVERSATION_LINEAGE_EDGES,
                  loading: false,
                  error: message,
                },
              },
              inflightByKey: {
                ...state.inflightByKey,
                [key]: undefined,
              },
            };
          });
          throw err;
        });

      set((state) => ({
        inflightByKey: {
          ...state.inflightByKey,
          [key]: request,
        },
      }));

      return request;
    },

    applyEdgeChange: (change) => {
      if (change.targetType !== "conversation") {
        return;
      }
      const key = conversationLineageKey(change.projectId, change.targetPublicId);
      set((state) => {
        const current = state.byKey[key] ?? emptyState();
        if (change.action === "detached") {
          const hasSubject = Boolean(change.subjectPublicId);
          const deleted = new Set(state.deletedEdgeIdsByKey[key] ?? []);
          deleted.add(change.edgeId);
          if (hasSubject) {
            for (const edge of current.edges) {
              if (sameConversationSubject(edge, change)) {
                deleted.add(edge.id);
              }
            }
          }
          const recent = new Set(state.recentRealtimeUpsertIdsByKey[key] ?? []);
          recent.delete(change.edgeId);
          if (hasSubject) {
            for (const edge of current.edges) {
              if (sameConversationSubject(edge, change)) {
                recent.delete(edge.id);
              }
            }
          }
          return {
            byKey: {
              ...state.byKey,
              [key]: {
                ...current,
                edges: current.edges.filter((edge) => {
                  if (edge.id === change.edgeId) return false;
                  if (hasSubject && sameConversationSubject(edge, change)) {
                    return false;
                  }
                  return true;
                }),
              },
            },
            deletedEdgeIdsByKey: {
              ...state.deletedEdgeIdsByKey,
              [key]: deleted,
            },
            recentRealtimeUpsertIdsByKey: {
              ...state.recentRealtimeUpsertIdsByKey,
              [key]: recent.size > 0 ? recent : undefined,
            },
          };
        }

        // Exclude the incoming edge id so a duplicate ATTACHED (REST create + STOMP)
        // is idempotent and does not self-tombstone / drop the recent-upsert mark.
        const replacedIds = current.edges
          .filter(
            (edge) =>
              sameConversationSubject(edge, change) && edge.id !== change.edgeId,
          )
          .map((edge) => edge.id);
        const withoutSubjectDup = current.edges.filter(
          (edge) => !sameConversationSubject(edge, change),
        );
        const nextEdge: LineageEdge = {
          id: change.edgeId,
          subjectType: change.subjectType,
          subjectPublicId: change.subjectPublicId,
          targetType: change.targetType,
          targetPublicId: change.targetPublicId,
        };
        const deleted = new Set(state.deletedEdgeIdsByKey[key] ?? []);
        deleted.delete(change.edgeId);
        for (const id of replacedIds) {
          deleted.add(id);
        }
        const recent = markRealtimeUpsert(
          state.recentRealtimeUpsertIdsByKey,
          key,
          change.edgeId,
        );
        const recentSet = new Set(recent[key] ?? []);
        for (const id of replacedIds) {
          recentSet.delete(id);
        }
        return {
          byKey: {
            ...state.byKey,
            [key]: {
              ...current,
              edges: [...withoutSubjectDup, nextEdge],
            },
          },
          recentRealtimeUpsertIdsByKey: {
            ...recent,
            [key]: recentSet,
          },
          deletedEdgeIdsByKey: {
            ...state.deletedEdgeIdsByKey,
            [key]: deleted.size > 0 ? deleted : undefined,
          },
        };
      });
    },

    clearConversation: (projectId, conversationId) => {
      const key = conversationLineageKey(projectId, conversationId);
      set((state) => {
        const epoch = (state.fetchEpochByKey[key] ?? 0) + 1;
        const { [key]: _removed, ...restByKey } = state.byKey;
        const { [key]: _inflight, ...restInflight } = state.inflightByKey;
        const { [key]: _recent, ...restRecent } = state.recentRealtimeUpsertIdsByKey;
        const { [key]: _deleted, ...restDeleted } = state.deletedEdgeIdsByKey;
        return {
          byKey: restByKey,
          inflightByKey: restInflight,
          fetchEpochByKey: {
            ...state.fetchEpochByKey,
            [key]: epoch,
          },
          recentRealtimeUpsertIdsByKey: restRecent,
          deletedEdgeIdsByKey: restDeleted,
        };
      });
    },

    invalidateConversation: (projectId, conversationId) =>
      get().fetchConversationLineage(projectId, conversationId, { force: true }),

    invalidateAllLoadedConversations: async () => {
      const keys = Object.keys(get().byKey);
      await Promise.all(
        keys.map((key) => {
          const [projectId, conversationId] = splitConversationLineageKey(key);
          if (!projectId || !conversationId) return Promise.resolve();
          return get().invalidateConversation(projectId, conversationId);
        }),
      );
    },
  }),
);
